import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GtmService } from '@df/module-gtm/gtm.service';
import { Status } from '@df/core/status';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { BasketDeliveryMethodsService } from './basket-delivery-methods.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Customer } from '@df/session/api/customer';
import { Radio2Component } from '../../ui/form/radio2/radio2.component';
import { RadioGroupDirective } from '@df/ui/form/radio/radio.component.def';
import { NBasketDeliveryMethods } from './basket-delivery-methods.namespace';

@Component({
    selector: 'basket-delivery-methods',
    templateUrl: './basket-delivery-methods.html',
    styleUrls: ['./basket-delivery-methods.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, FormsModule, TranslationModule, PriceComponent, Radio2Component, RadioGroupDirective]
})
export class BasketDeliveryMethodsComponent extends UiComponent implements OnInit {
    @Output()
    methodSelected = new EventEmitter<Checkout.IShippingMethod>();

    protected methods: Checkout.IShippingMethod[] = [];

    private loadStatus = new Status();

    private selectedMethodCode?: string;

    private deliveryMethodsService = inject(BasketDeliveryMethodsService);

    private gtmService = inject(GtmService);

    private customer = inject(Customer);

    override ngOnInit(): void {
        super.ngOnInit();

        this.subscriptions.add(this.loadStatus.subscribe(() => this.detectChanges()));
        this.subscriptions.add(
            this.customer.basket.subscribe(() => {
                this.loadDeliveryMethods();
            })
        );

        this.loadDeliveryMethods();
    }

    private get shippingMethodCodeFromBasket(): string | undefined {
        return this.customer.basket?.data?.shippingMethod;
    }

    private async loadDeliveryMethods() {
        let response: NBasketDeliveryMethods.IResponse | undefined = undefined;
        try {
            response = await this.deliveryMethodsService.getDeliveryMethods(this.loadStatus);
        } catch (error) {}

        this.methods = response?.rates || [];

        this.methods.sort((a, b) => {
            const priceA = a.price ?? 0;
            const priceB = b.price ?? 0;

            if (priceA === 0 && priceB > 0) return 1;
            if (priceA > 0 && priceB === 0) return -1;

            if (priceA === 0 && priceB === 0) {
                const aIsCollection = this.deliveryMethodsService.isCollectionMethod(a);
                const bIsCollection = this.deliveryMethodsService.isCollectionMethod(b);

                if (aIsCollection !== bIsCollection) {
                    return aIsCollection ? 1 : -1;
                }
            }

            return 0;
        });

        const previouslySelectedCode = this.selectedMethodCode;

        if (!previouslySelectedCode || !this.methods.find(i => i.code === previouslySelectedCode)) {
            const basketShippingMethod = this.methods.find(i => i.code == this.shippingMethodCodeFromBasket);
            this.selectedMethodCode = basketShippingMethod?.code;
            this.onMethodSelectionChange(false);
        }
        this.detectChanges();
    }

    protected onMethodSelectionChange(gtmEvent = true): void {
        const selectedMethod = this.methods.find(m => m.code === this.selectedMethodCode);
        if (!selectedMethod) {
            return;
        }

        const isCollection = this.deliveryMethodsService.isCollectionMethod(selectedMethod);
        if (gtmEvent) {
            const price = selectedMethod.price || 0;
            this.gtmService.sendEventGA4('delivery_method_selected', {
                delivery_method_code: selectedMethod.code,
                delivery_method_title: selectedMethod.title,
                delivery_method_price: price,
                delivery_method_type: isCollection ? NBasketDeliveryMethods.EType.COLLECTION : NBasketDeliveryMethods.EType.DELIVERY,
                is_free: price === 0,
                page_location: 'basket'
            });
        }

        this.methodSelected.emit(selectedMethod);
    }

    protected get shouldShowSelector(): boolean {
        return !!this.methods.length;
    }

    protected get shouldShowNoMethodsMessage(): boolean {
        return !this.shouldShowSelector && !this.loadStatus.busy;
    }
}
